// Eskom PARCC — XLSX glue for the export builders (thin, separate from the pure
// row builders so the row/column logic stays unit-tested as data).
//
// The pure builders return string-row intermediates (`ComplianceSheet[]` /
// `string[][]`); this module turns those into an XLSX WorkBook via `xlsx`. The
// binary output is intentionally NOT unit-tested — only the row data is.
import * as XLSX from 'xlsx';
import type { ComplianceSheet } from './buildComplianceWorkbook';

/** A reasonable column-width hint: wider for free-text/criteria columns. */
function colWidth(header: string): number {
  const wide = /Finding|Criteria|Description|Notes|Stage|Updated|Initial|Comments/.test(header);
  return Math.min(Math.max(header.length + 4, 14), wide ? 55 : 28);
}

/**
 * Apply per-column widths to a worksheet built from AOA.
 *
 * NOTE: we deliberately do NOT set a frozen header pane. The community build of
 * `xlsx` (SheetJS) has no freeze-pane WRITER, so the old `ws['!freeze']` was a
 * no-op — and writing a non-standard worksheet property is exactly the kind of
 * thing Excel flags as "we found a problem with some content … repaired
 * worksheet properties". Column widths (`!cols`) are fully supported.
 */
function decorate(ws: XLSX.WorkSheet, header: readonly string[]): void {
  ws['!cols'] = header.map((h) => ({ wch: colWidth(h) }));
}

/**
 * Turn the per-route {@link ComplianceSheet} intermediates into an XLSX WorkBook:
 * one worksheet per route, each from its `rows` array-of-arrays (row 0 is the
 * header). Header rows are frozen and columns sized. Sheet names are assumed
 * already Excel-safe + unique (the builder's `cleanSheetName` guarantees this).
 */
export function workbookFromSheets(sheets: ComplianceSheet[]): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  for (const { sheetName, rows } of sheets) {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    if (rows.length > 0) decorate(ws, rows[0]);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  }
  return wb;
}

/**
 * Like {@link workbookFromSheet}, plus an AutoFilter across the header row —
 * the "filter-friendly" working-paper export (9 Jun call): users drill to a
 * transaction / procedure / result and hide columns in Excel directly. The
 * community SheetJS build writes `!autofilter` natively.
 */
export function workbookFromFilterSheet(name: string, rows: string[][]): XLSX.WorkBook {
  const wb = workbookFromSheet(name, rows);
  const ws = wb.Sheets[name];
  if (rows.length > 0 && rows[0].length > 0) {
    const ref = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: Math.max(rows.length - 1, 0), c: rows[0].length - 1 },
    });
    ws['!autofilter'] = { ref };
  }
  return wb;
}

/**
 * Turn a single flat sheet (header row + data rows) into a one-worksheet
 * WorkBook. Used for the reviewer working-paper export.
 */
export function workbookFromSheet(name: string, rows: string[][]): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(rows);
  if (rows.length > 0) decorate(ws, rows[0]);
  XLSX.utils.book_append_sheet(wb, ws, name);
  return wb;
}

/**
 * Column widths for the working-paper DOCUMENT layout. These sheets stack mixed
 * rows — 2-column "Label │ value" pairs and the 7-column checklist table — so a
 * single column set must serve both: A is the label / checklist "No"; B onward
 * are sized for the checklist (Stage, Sub-Criteria, Risk, Result, Finding,
 * Action), which also leaves the wide B–C columns roomy for key/value text.
 */
const DOCUMENT_COL_WIDTHS = [24, 60, 60, 18, 24, 45, 35];

/**
 * Turn the per-route working-paper DOCUMENT intermediates into an XLSX WorkBook —
 * one worksheet per route, each a stack of document blocks (array-of-arrays).
 * Unlike {@link workbookFromSheets}, NO header row is frozen (these sheets have no
 * single header — each block carries its own bands); columns are sized for the
 * document layout. Sheet names are assumed already Excel-safe + unique.
 */
export function workbookFromDocuments(sheets: ComplianceSheet[]): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  const cols = DOCUMENT_COL_WIDTHS.map((wch) => ({ wch }));
  for (const { sheetName, rows } of sheets) {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = cols;
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  }
  return wb;
}
